/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Fragment, useContext, useRef } from "react";

import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";

import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import DropzoneContext from "../../context/dropzone/dropzoneContext";
import { FILE_COUNT } from "../../shared";
import BannerMessage from "../BannerMessage/BannerMessage";
import Button from "../Button/Button";
import Tooltip from "../Tooltip/Tooltip";
import { useStyles } from "./styles";
import { FrameProps } from "./types";

const Frame = ({
  isDragged,
  supportedFormats,
  getRootProps,
  getInputProps,
  isMultiple,
  isDisabled,
  tooltipText,
  isUploaded
}: FrameProps) => {
  const { messages } = useContext(DropzoneContext);
  const { blue700, gray700, textDisabled } = colors;

  const { t } = useTranslation();

  const onDragEnterRef = useRef(null);
  const onDragEnterLeaveRef = useRef(null);

  const textColor = isDisabled ? textDisabled : gray700;

  const { frameContentCSS } = useStyles(isDragged, isDisabled);

  const multipleCheck = isMultiple ? FILE_COUNT.Files : FILE_COUNT.File;

  const getFileSelection = () => {
    if (supportedFormats.length === 0) {
      return <div>{t("Dropzone##select file type")}</div>;
    } else {
      return (
        <Fragment>
          <div>{t(`Dropzone##or drop ${multipleCheck} to upload`)}</div>

          <div>
            {t("Dropzone##supported formats")} {supportedFormats.join(", ")}
          </div>
        </Fragment>
      );
    }
  };

  return (
    <Fragment>
      <Typography
        variant="body2Medium"
        sx={{ color: gray700, marginBottom: "4px" }}
      >
        {isUploaded ? t("Dialog##selected files") : t("Dropzone##add file")}:
      </Typography>

      {messages?.map(({ status, title, description }) => (
        <BannerMessage
          key={title}
          status={status}
          title={title}
          description={description}
          css={css({
            marginBottom: "4px"
          })}
        />
      ))}

      {isUploaded ? (
        <div></div>
      ) : (
        <Tooltip placement="top" size="medium" title={tooltipText ?? ""}>
          <div {...getRootProps()} css={css(frameContentCSS)}>
            <CSSTransition
              in={isDragged}
              unmountOnExit
              timeout={{ enter: 300 }}
              nodeRef={onDragEnterRef}
              classNames="on-drag-enter"
            >
              <Typography
                variant="body2Medium"
                sx={{ color: blue700 }}
                ref={onDragEnterRef}
              >
                {t(`Dropzone##drop ${multipleCheck} to upload`)}
              </Typography>
            </CSSTransition>

            <CSSTransition
              in={!isDragged}
              unmountOnExit
              timeout={{ enter: 300 }}
              classNames="on-drag-leave"
              nodeRef={onDragEnterLeaveRef}
            >
              <div ref={onDragEnterLeaveRef}>
                <Button
                  data-testid="select-file-button"
                  color="primarySubtle"
                  size="small"
                  variant="textOnly"
                  disabled={isDisabled}
                  css={css({
                    marginBottom: "4px"
                  })}
                >
                  {t(`Dropzone##select ${multipleCheck}`)}
                </Button>

                <Typography
                  component={"div"}
                  variant="body2Medium"
                  sx={{
                    color: textColor,

                    "& > div:last-of-type": {
                      fontWeight: 400
                    }
                  }}
                >
                  {getFileSelection()}
                </Typography>
              </div>
            </CSSTransition>

            <input {...getInputProps()} />
          </div>
        </Tooltip>
      )}
    </Fragment>
  );
};

export default Frame;
